import { useEffect } from 'react';

/**
 * 일시 토스트 (D-09 §15.8) — 비차단 알림. 화면 하단 가장자리에 노출.
 *
 * ui-core에 대응 컴포넌트가 없고, 앱 전역 토스트(shell sonner)는 이 에이전트 범위(특히 게스트
 * 공유 화면 — AppFrame 바깥)에서 쓸 수 없어 로컬로 둔다. 색·반경·그림자·z-index는 전부 토큰
 * (D-07 §3.2 — 임의값 금지). 색만으로 의미를 전달하지 않고 아이콘+텍스트를 동반한다(§3.2 #5).
 *
 * - `variant`: 'warning'(경고, 기본) / 'info'(처리중·안내) / 'success'(처리 완료 — 표준 .toast-success)
 * - `duration`: 자동 소멸(ms). 기본 3초. **null이면 자동 소멸 안 함**(처리중 표시 등, 호출부가 직접 내린다).
 */
type ToastVariant = 'warning' | 'info' | 'success';

const TONE: Record<ToastVariant, { bg: string; fg: string; border: string }> = {
  warning: { bg: 'var(--color-warningSubtle)', fg: 'var(--color-warningStrong)', border: 'var(--color-warning)' },
  info: { bg: 'var(--color-infoSubtle)', fg: 'var(--color-infoStrong)', border: 'var(--color-info)' },
  success: { bg: 'var(--color-successSubtle)', fg: 'var(--color-successStrong)', border: 'var(--color-success)' },
};

export function Toast({
  message,
  onClose,
  duration = 3000,
  variant = 'warning',
}: {
  message: string | null;
  onClose: () => void;
  duration?: number | null;
  variant?: ToastVariant;
}) {
  useEffect(() => {
    if (!message || duration == null) return;
    const t = window.setTimeout(onClose, duration);
    return () => window.clearTimeout(t);
  }, [message, duration, onClose]);

  if (!message) return null;

  const tone = TONE[variant];

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-2 px-4 py-2.5 text-[13px] font-medium"
      style={{
        zIndex: 'var(--z-toast)',
        maxWidth: 'calc(100% - 32px)',
        background: tone.bg,
        color: tone.fg,
        border: `var(--border-width-hairline) solid ${tone.border}`,
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elevation-2)',
        whiteSpace: 'pre-line',
      }}
    >
      {variant === 'info' ? (
        // 처리중 — 회전 스피너
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={tone.border} strokeWidth={2.5} strokeLinecap="round" className="shrink-0 animate-spin" aria-hidden>
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
      ) : variant === 'success' ? (
        // 처리 완료 — 체크 원
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={tone.border} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="shrink-0" aria-hidden>
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={tone.border} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="shrink-0" aria-hidden>
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      )}
      <span>{message}</span>
    </div>
  );
}
